import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { api } from '../services/api';
import type { UserPublic } from 'shared/types/index.js';

interface AuthState {
  user: UserPublic | null;
  isLoading: boolean;
  error: string | null;
  login: (loginId: string, password: string) => Promise<boolean>;
  register: (data: { login_id: string; password: string; name: string; email: string }) => Promise<boolean>;
  loginWithTokens: (accessToken: string, refreshToken: string) => Promise<boolean>;
  logout: () => Promise<void>;
  fetchMe: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    (set) => ({
      user: null,
      isLoading: false,
      error: null,

      login: async (loginId: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const result = await api.login({ login_id: loginId, password });
          if (result.success && result.data) {
            set({ user: result.data.user as unknown as UserPublic, isLoading: false });
            return true;
          }
          set({ error: result.error || 'ログインに失敗しました', isLoading: false });
          return false;
        } catch (e) {
          set({ error: (e as Error).message, isLoading: false });
          return false;
        }
      },

      register: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const result = await api.register(data);
          if (result.success && result.data) {
            set({ user: result.data.user as unknown as UserPublic, isLoading: false });
            return true;
          }
          set({ error: result.error || '登録に失敗しました', isLoading: false });
          return false;
        } catch (e) {
          set({ error: (e as Error).message, isLoading: false });
          return false;
        }
      },

      loginWithTokens: async (accessToken: string, refreshToken: string) => {
        api.setTokens(accessToken, refreshToken);
        try {
          const result = await api.getMe();
          if (result.success && result.data) {
            set({ user: result.data });
            return true;
          }
        } catch {
          api.clearTokens();
        }
        return false;
      },

      logout: async () => {
        try {
          await api.logout();
        } finally {
          set({ user: null });
        }
      },

      fetchMe: async () => {
        if (!api.isAuthenticated()) return;
        try {
          const result = await api.getMe();
          if (result.success && result.data) {
            set({ user: result.data });
          }
        } catch {
          set({ user: null });
        }
      },

      clearError: () => set({ error: null }),
    }),
    { name: 'AuthStore' }
  )
);
